import PropTypes from 'prop-types'
import { sliceStatusPropTypes } from '../slice.types'

/**
 * @typedef {Object} StoreView
 * @property {number} id - Unique identifier
 * @property {string} name - Location name
 * @property {number} portal_id - Associated portal ID
 * @property {string|null} description - Location description
 * @property {number|null} external_id - External system ID
 * @property {Date|null} external_updated_at - Last external update timestamp
 * @property {Date|null} discarded_at - Soft delete timestamp
 * @property {Date} created_at - Creation timestamp
 * @property {Date} updated_at - Last update timestamp
 *
 * @property {number|null} order - Display order
 * @property {boolean|null} is_active - Whether location is active
 * @property {boolean} is_default - Whether this is the default location (default: false)
 * @property {string|null} location_code - Unique location code
 * @property {number|null} order_count - Number of orders
 * @property {number|null} sort_order - Sort order for display
 *
 * @property {boolean} coop_enable - Enable cooperative features (default: false)
 * @property {boolean} coop_overwrite - Overwrite cooperative settings (default: false)
 * @property {number} coop_percentage_100 - Cooperative percentage (default: 0)
 *
 * @property {boolean} show_price - Show prices to users (default: true)
 * @property {boolean} hide_header_menu - Hide header menu (default: false)
 * @property {boolean} show_stock_count - Show stock counts (default: false)
 * @property {boolean} hide_products_out_of_stock - Hide out of stock products (default: false)
 *
 * @property {string|null} checkout_information_html - Custom checkout HTML
 * @property {boolean} show_checkout_information - Show checkout info (default: false)
 * @property {boolean} show_customer_note_input - Show customer note input (default: false)
 * @property {boolean} customer_note_input_required - Require customer notes (default: false)
 * @property {number|null} custom_checkout_fee - Custom checkout fee
 * @property {string|null} custom_checkout_fee_alias - Custom fee alias
 *
 * @property {boolean} requires_approval - Require order approval (default: false)
 * @property {boolean} all_products_require_approval - All products need approval (default: false)
 *
 * @property {string} default_fulfillment_status - Default fulfillment status (default: "Awaiting Fulfilment")
 *
 * @property {number} conversion_ratio - Conversion ratio (default: 1.0)
 *
 * @property {string[]|null} billing_poc_payments - Billing POC payment types
 * @property {string|null} billing_poc_emails - Billing POC email addresses
 * @property {string|null} location_poc_emails - Location POC email addresses
 *
 * @property {string} custom_invoice_message - Custom invoice message (default: "Please see your invoice and remit payment at your earliest convenience. Thank you.")
 * @property {boolean} disable_client_side_invoicing - Disable client-side invoicing (default: false)
 *
 * @property {number|null} checkout_form_id - Associated checkout form ID
 * @property {number|null} location_group_id - Location group ID
 * @property {number|null} new_location_group_id - New location group ID
 *
 * @property {string} status - Location status (default: "Assigned")
 * @property {boolean} require_billing_address_for_free_orders - Require billing for free orders (default: false)
 *
 * @property {number} agency_id - Agency ID (required)
 *
 * @property {number} punchout - Punchout setting (0: disabled, 1: only, 2: hybrid, default: 0)
 *
 * @property {StoreView['pages']} pages - Pages
 * @property {StoreView['categories']} categories - Categories
 * @property {StoreView['checkout_form']} checkout_form - Checkout form
 */

/**
 * @typedef {Object} StoreViewsState
 * @property {StoreView} current
 * @property {StoreView[]} available
 */

/** @typedef {import('../slice.types').BaseSlice & StoreViewsState} StoreViewsSlice */

/** @type {StoreViewsSlice} */
export const initialStoreViewsState = {
  status: 'init',
  errors: [],
  current: null,
  available: []
}

/** @enum {string} */
export const STORE_VIEWS_ACTIONS = {
  SET_STORE_VIEWS_STATUS: 'SET_STORE_VIEWS_STATUS',
  SET_STORE_VIEWS_ERRORS: 'SET_STORE_VIEWS_ERRORS',
  SET_STORE_VIEWS_CURRENT: 'SET_STORE_VIEWS_CURRENT',
  SET_STORE_VIEWS_AVAILABLE: 'SET_STORE_VIEWS_AVAILABLE',

  CLEAR_STORE_VIEWS_ERRORS: 'CLEAR_STORE_VIEWS_ERRORS',
  CLEAR_STORE_VIEWS_CURRENT: 'CLEAR_STORE_VIEWS_CURRENT',
  CLEAR_STORE_VIEWS_AVAILABLE: 'CLEAR_STORE_VIEWS_AVAILABLE'
}

export const currentStoreViewPropType = PropTypes.shape({
  id: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
  portal_id: PropTypes.number.isRequired,
  description: PropTypes.string,
  external_id: PropTypes.string,
  external_updated_at: PropTypes.string,
  discarded_at: PropTypes.string,
  created_at: PropTypes.string,
  updated_at: PropTypes.string,
  order: PropTypes.number,
  is_active: PropTypes.bool,
  is_default: PropTypes.bool,
  location_code: PropTypes.string,
  order_count: PropTypes.number,
  sort_order: PropTypes.number,
  coop_enable: PropTypes.bool,
  coop_overwrite: PropTypes.bool,
  coop_percentage_100: PropTypes.number,
  show_price: PropTypes.bool,
  hide_header_menu: PropTypes.bool,
  show_stock_count: PropTypes.bool,
  hide_products_out_of_stock: PropTypes.bool,
  checkout_information_html: PropTypes.string,
  show_checkout_information: PropTypes.bool,
  show_customer_note_input: PropTypes.bool,
  customer_note_input_required: PropTypes.bool,
  custom_checkout_fee: PropTypes.number,
  custom_checkout_fee_alias: PropTypes.string,
  requires_approval: PropTypes.bool,
  all_products_require_approval: PropTypes.bool,
  default_fulfillment_status: PropTypes.string,
  conversion_ratio: PropTypes.number,
  billing_poc_payments: PropTypes.arrayOf(PropTypes.string),
  billing_poc_emails: PropTypes.string,
  location_poc_emails: PropTypes.string,
  custom_invoice_message: PropTypes.string,
  disable_client_side_invoicing: PropTypes.bool,
  checkout_form_id: PropTypes.number,
  location_group_id: PropTypes.number,
  new_location_group_id: PropTypes.number,
  status: PropTypes.string,
  require_billing_address_for_free_orders: PropTypes.bool,
  agency_id: PropTypes.number,
  punchout: PropTypes.number,
  pages: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      url: PropTypes.string,
      page_type: PropTypes.string,
      location_id: PropTypes.number
    })
  ),
  categories: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      location_id: PropTypes.number
    })
  ),
  checkout_form: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    location_id: PropTypes.number
  })
})
export const availableStoreViewsPropType = PropTypes.arrayOf(currentStoreViewPropType)
export const storeViewsSlicePropType = PropTypes.shape({
  status: sliceStatusPropTypes,
  errors: PropTypes.arrayOf(PropTypes.shape({ message: PropTypes.string })),
  current: currentStoreViewPropType,
  available: availableStoreViewsPropType
})

/**
 * Base slice actions
 * @typedef {{ type: STORE_VIEWS_ACTIONS, payload: StoreViewsSlice['status'] }} StoreViewsStatusAction
 * @typedef {{ type: STORE_VIEWS_ACTIONS, payload: StoreViewsSlice['errors'] }} StoreViewsErrorsAction
 *
 * Locations slice actions
 * @typedef {{ type: STORE_VIEWS_ACTIONS, payload: StoreViewsSlice['current'] }} StoreViewsCurrentAction
 * @typedef {{ type: STORE_VIEWS_ACTIONS, payload: StoreViewsSlice['available'] }} StoreViewsAvailableAction
 *
 * Clear action
 * @typedef {{ type: STORE_VIEWS_ACTIONS }} StoreViewsClearAction
 *
 * @typedef {StoreViewsStatusAction | StoreViewsErrorsAction | StoreViewsCurrentAction | StoreViewsAvailableAction | StoreViewsClearAction} StoreViewsAction
 * */
